import React, { useMemo } from "react";
import { ScreenShell } from "../components";
import { T } from "../theme";
import {
  autoSprintName,
  bmrFor,
  chartEndpointsForPlan,
  dailyTargetFor,
  effectivePaceLbsPerWeek,
  projectedEndDateYmd,
  tdeeFor,
} from "../calc";
import { displayWeight } from "../units";
import type { GoalDirection, OnboardingDraft } from "../types";

type Props = {
  step: number;
  total: number;
  draft: OnboardingDraft;
  goal: GoalDirection;
  onSavePlan: () => void;
  onEdit: () => void;
  onBack: () => void;
  isMobile: boolean;
};

function fmtDate(ymd: string): string {
  const d = new Date(ymd + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
}

function fmtShort(ymd: string): string {
  const d = new Date(ymd + "T00:00:00");
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

const GOAL_KIND: Record<GoalDirection, string> = { lose: "Cut", gain: "Bulk", maintain: "Maintain" };

export function PlanRevealScreen({ step, total, draft, goal, onSavePlan, onEdit, onBack, isMobile }: Props): React.ReactElement {
  const { bmr, tdee, dailyTarget, endYmd, sprintName } = useMemo(() => {
    const sex = draft.sex ?? "X";
    const bmrV = bmrFor({
      weightLbs: draft.currentWeightLbs,
      heightIn: draft.heightIn,
      sex,
      birthday: draft.birthday,
    }) ?? 0;
    const tdeeV = bmrV > 0 ? tdeeFor(bmrV, draft.activityFactor) : 0;
    const target = tdeeV > 0 ? dailyTargetFor({ goal, paceLbsPerWeek: draft.paceLbsPerWeekAbs, tdee: tdeeV, sex }) : 0;
    const effPace = tdeeV > 0
      ? effectivePaceLbsPerWeek({ paceLbsPerWeekAbs: draft.paceLbsPerWeekAbs, tdee: tdeeV, sex, goal })
      : draft.paceLbsPerWeekAbs;
    const endYmdV =
      goal === "maintain"
        ? ""
        : projectedEndDateYmd(draft.currentWeightLbs, draft.targetWeightLbs, effPace);
    const name = autoSprintName(goal, endYmdV || new Date().toISOString().slice(0, 10));
    return { bmr: bmrV, tdee: tdeeV, dailyTarget: target, endYmd: endYmdV, sprintName: name };
  }, [draft, goal]);

  const netLb = draft.targetWeightLbs - draft.currentWeightLbs;
  const sign = netLb > 0 ? "+" : "−";

  // Linear projection — slope is illustrative of the pace (faster = steeper).
  const Y_TOP = 18;
  const Y_BOT = 92;
  const { y0, y1 } = chartEndpointsForPlan(goal, draft.paceLbsPerWeekAbs);
  const linePath = `M0,${y0} L316,${y1}`;
  const fillPath = `M0,${y0} L316,${y1} L320,${y1} L320,110 L0,110 Z`;

  return (
    <ScreenShell
      step={step}
      total={total}
      title="Your plan."
      subtitle="Daily target recalibrates with your actual weigh-ins."
      onBack={onBack}
      isMobile={isMobile}
      primaryLabel="Save my plan"
      onPrimary={onSavePlan}
    >
      <div
        style={{
          background: T.bgCard,
          border: `1px solid ${T.hairline}`,
          borderRadius: 20,
          padding: isMobile ? "20px 20px" : "26px 30px",
        }}
      >
        {/* Sprint pill + Edit link on one row — saves vertical space */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <div
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
              padding: "4px 10px",
              borderRadius: 999,
              background: "rgba(132,204,22,0.10)",
              border: "1px solid rgba(132,204,22,0.32)",
              color: "#d9f99d",
              fontSize: "0.72rem",
              fontWeight: 800,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            <span aria-hidden style={{ width: 6, height: 6, borderRadius: 999, background: T.lime, flexShrink: 0 }} />
            Sprint 01 · {sprintName}
          </div>
          <button
            type="button"
            onClick={onEdit}
            style={{
              background: "transparent",
              border: "none",
              color: T.fg2,
              fontSize: "0.78rem",
              fontWeight: 700,
              cursor: "pointer",
              padding: 4,
              textDecoration: "underline",
              textUnderlineOffset: 3,
              textDecorationColor: "rgba(255,255,255,0.22)",
              flexShrink: 0,
            }}
          >
            Edit goal
          </button>
        </div>

        <div style={{ marginTop: 14, fontSize: "0.64rem", fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: T.muted }}>
          Daily intake
        </div>
        <div
          style={{
            marginTop: 6,
            fontSize: isMobile ? "2.9rem" : "3.8rem",
            fontWeight: 700,
            letterSpacing: "-0.03em",
            lineHeight: 1,
            ...T.tabular,
            background: T.ctaGrad,
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            backgroundClip: "text",
          }}
        >
          {dailyTarget.toLocaleString()}
          <span
            style={{
              fontSize: "1.0rem",
              color: T.muted,
              fontWeight: 700,
              marginLeft: 8,
              background: "none",
              WebkitTextFillColor: T.muted,
            }}
          >
            kcal
          </span>
        </div>

        {dailyTarget > 0 && dailyTarget < ((draft.sex ?? "X") === "F" ? 1200 : 1500) ? (
          <div style={{ marginTop: 8 }}>
            <span style={{
              display: "inline-flex",
              alignItems: "center",
              padding: "0.18rem 0.5rem",
              borderRadius: 999,
              fontSize: "0.7rem",
              fontWeight: 700,
              color: "rgba(253,224,71,0.95)",
              border: "1px solid rgba(251,191,36,0.45)",
              letterSpacing: "0.02em",
            }}>Low intake</span>
          </div>
        ) : null}

        {goal !== "maintain" && endYmd ? (
          <div style={{ marginTop: 8, color: T.fg2, fontWeight: 600, fontSize: "0.88rem" }}>
            Hits {displayWeight(draft.targetWeightLbs, draft.unitsWeight)} {draft.unitsWeight} on{" "}
            <span style={{ color: T.fg, fontWeight: 800 }}>{fmtDate(endYmd)}</span>
          </div>
        ) : goal === "maintain" ? (
          <div style={{ marginTop: 8, color: T.fg2, fontWeight: 600, fontSize: "0.88rem" }}>
            Maintaining at{" "}
            <span style={{ color: T.fg, fontWeight: 800 }}>
              {displayWeight(draft.currentWeightLbs, draft.unitsWeight)} {draft.unitsWeight}
            </span>
          </div>
        ) : null}

        {/* Trend chart */}
        <div style={{ position: "relative", marginTop: 18, height: 124 }}>
          <svg viewBox="0 0 320 110" width="100%" height="106" preserveAspectRatio="none" style={{ display: "block" }}>
            <defs>
              <linearGradient id="pg-onboarding-reveal" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor="#3B82F6" stopOpacity="0.22" />
                <stop offset="100%" stopColor="#3B82F6" stopOpacity="0" />
              </linearGradient>
            </defs>
            {[Y_TOP, (Y_TOP + Y_BOT) / 2, Y_BOT].map((y) => (
              <line key={y} x1="0" y1={y} x2="320" y2={y} stroke="rgba(255,255,255,0.06)" strokeDasharray="2 6" />
            ))}
            <path d={fillPath} fill="url(#pg-onboarding-reveal)" />
            <path d={linePath} fill="none" stroke="#3B82F6" strokeWidth="2" strokeLinecap="round" />
            <circle cx="0" cy={y0} r="4.5" fill="#fff" />
            <circle cx="316" cy={y1} r="5.5" fill="#84CC16" />
          </svg>
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              marginTop: 4,
              fontSize: "0.7rem",
              fontWeight: 700,
              color: T.muted,
              ...T.tabular,
              letterSpacing: "-0.005em",
            }}
          >
            <span>
              Today · {displayWeight(draft.currentWeightLbs, draft.unitsWeight)} {draft.unitsWeight}
            </span>
            <span style={{ color: "#a3e635" }}>
              {endYmd
                ? `${fmtShort(endYmd)} · ${displayWeight(draft.targetWeightLbs, draft.unitsWeight)} ${draft.unitsWeight}`
                : `Holding · ${displayWeight(draft.currentWeightLbs, draft.unitsWeight)} ${draft.unitsWeight}`}
            </span>
          </div>
        </div>

        {/* KPI grid */}
        <div
          style={{
            marginTop: 18,
            display: "grid",
            gridTemplateColumns: "1fr 1fr 1fr",
            gap: 10,
          }}
        >
          {[
            { l: "Pace", v: goal === "maintain" ? "0.0" : `${sign}${draft.paceLbsPerWeekAbs.toFixed(1)}`, s: "lb/wk" },
            { l: "Net", v: goal === "maintain" ? "0.0" : `${sign}${Math.abs(netLb).toFixed(1)}`, s: "lb" },
            { l: "TDEE", v: tdee > 0 ? Math.round(tdee).toLocaleString() : "—", s: "kcal" },
          ].map((k) => (
            <div
              key={k.l}
              style={{
                background: "rgba(255,255,255,0.025)",
                border: `1px solid ${T.hairline}`,
                borderRadius: 12,
                padding: "10px 12px",
              }}
            >
              <div style={{ fontSize: "0.62rem", fontWeight: 700, letterSpacing: "0.09em", textTransform: "uppercase", color: T.muted }}>
                {k.l}
              </div>
              <div
                style={{
                  fontSize: "1.1rem",
                  fontWeight: 800,
                  ...T.tabular,
                  lineHeight: 1.2,
                  marginTop: 3,
                  letterSpacing: "-0.01em",
                  color: T.fg,
                }}
              >
                {k.v}
                <span style={{ fontSize: "0.62rem", color: T.muted, fontWeight: 700, marginLeft: 3 }}>{k.s}</span>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ marginTop: 12, color: T.muted, fontSize: "0.74rem", textAlign: "center", lineHeight: 1.5 }}>
        Resting burn ≈ {bmr > 0 ? Math.round(bmr).toLocaleString() : "—"} kcal/day · {draft.activityFactor.toFixed(2)}× activity · {GOAL_KIND[goal]} sprint
      </div>
    </ScreenShell>
  );
}
